import { useEffect, useState } from "react";
import { adminHttp, resolveImageUrl } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import BlogForm from "@/components/admin/BlogForm";

const EMPTY = {
  slug: "",
  title: "",
  excerpt: "",
  body: "",
  category: "Premium Projects",
  cover_image: "",
  read_minutes: 5,
  published_at: new Date().toISOString().slice(0, 10),
};

export default function BlogsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    adminHttp.get("/blogs").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const onDelete = async (slug) => {
    if (!window.confirm(`Delete article "${slug}"?`)) return;
    try {
      await adminHttp.delete(`/admin/blogs/${slug}`);
      toast.success("Article deleted.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  };

  if (editing === "new") {
    return <BlogForm initial={EMPTY} mode="create" onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />;
  }
  if (editing) {
    const item = items.find((b) => b.slug === editing);
    if (item) return <BlogForm initial={item} mode="edit" onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />;
  }

  return (
    <div data-testid="admin-blogs-panel">
      <div className="flex items-center justify-between mb-10">
        <div>
          <div className="underline-label mb-2">Editorial</div>
          <h2 className="font-display text-3xl text-[#1c1c1c]">Journal</h2>
        </div>
        <button
          data-testid="admin-blog-new"
          onClick={() => setEditing("new")}
          className="inline-flex items-center gap-3 px-5 py-3 bg-[#c5a059] text-black uppercase text-[10px] tracking-[0.32em] hover:bg-[#f5f0e8] transition-colors"
        >
          <Plus size={14} /> New Article
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[#6a6259] text-sm">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="text-[#6a6259] text-sm">No articles yet.</div>
      ) : (
        <div className="space-y-3">
          {items.map((b) => (
            <div
              key={b.slug}
              data-testid={`admin-blog-row-${b.slug}`}
              className="grid grid-cols-12 gap-4 items-center p-4 border border-[#1c1c1c]/10 bg-white/70 hover:border-[#c5a059]/40 transition-colors"
            >
              <div className="col-span-2 aspect-[4/3] bg-[#ede5d5] overflow-hidden">
                {b.cover_image && (
                  <img src={resolveImageUrl(b.cover_image)} alt={b.title} className="w-full h-full object-cover" />
                )}
              </div>
              <div className="col-span-8">
                <div className="underline-label mb-1 text-[10px]">{b.category} · {b.read_minutes} min</div>
                <div className="font-display text-xl text-[#1c1c1c]">{b.title}</div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#6a6259] mt-1">Published {b.published_at}</div>
              </div>
              <div className="col-span-2 flex flex-col gap-2 items-end">
                <button
                  data-testid={`admin-blog-edit-${b.slug}`}
                  onClick={() => setEditing(b.slug)}
                  className="p-2 text-[#c5a059] hover:bg-[#c5a059] hover:text-black transition-colors"
                  aria-label="Edit"
                >
                  <Pencil size={14} />
                </button>
                <button
                  data-testid={`admin-blog-delete-${b.slug}`}
                  onClick={() => onDelete(b.slug)}
                  className="p-2 text-[#6a6259] hover:bg-red-900 hover:text-white transition-colors"
                  aria-label="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
